'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import LanguageSwitcher from '../../../../components/LanguageSwitcher';
import ArtigoToc, { HEADER_OFFSET, type TocItem } from '../../../../components/ArtigoToc';
import ArticleContentStyles from '../../../../components/ArticleContentStyles';
import { isConteudoPdf } from '../../../../lib/articleContent';
import { Artigo, dataArtigoBr } from '../../../../lib/blog-api';

// Largura fixa (px) que o pdftohtml usa para cada pagina (A4 a zoom 1.5x -
// ver pdf_extract.php no chefia). O HTML convertido de um PDF usa
// coordenadas absolutas dentro dessa largura; sem escalar, o texto
// "vaza" para fora do container em telas menores que isso, sobrepondo
// colunas (foi o bug reportado). PdfHtmlContent mede a largura real
// disponivel e aplica transform:scale() para cada pagina caber, mantendo
// a proporcao e sem exigir alterar as coordenadas geradas pelo PDF.
const PDF_HTML_PAGE_WIDTH = 918;

/**
 * Divide o HTML gerado pelo pdftohtml em blocos por pagina, usando o
 * marcador "<!-- Page N -->" que o poppler emite antes de cada pagina.
 * Sem isso, um documento longo (visto na pratica: uma lei de 151
 * paginas, ~1.8MB de HTML + ~1.8MB de imagens de fundo) era injetado
 * inteiro de uma vez via dangerouslySetInnerHTML - pagina lenta/travando
 * ao montar todo o DOM e todas as 151 imagens juntas, sem nenhum jeito
 * de pular para uma pagina especifica. Documentos curtos (a maioria dos
 * e-books) tambem passam por aqui, so que com poucas paginas.
 */
function splitPdfHtmlPages(html: string): string[] {
  const pages = html.split(/(?=<!-- Page \d+ -->)/g).filter((p) => p.trim() !== '');
  return pages.length > 0 ? pages : [html];
}

function PdfHtmlContent({ html }: { html: string }) {
  const t = useTranslations('paginaArtigo');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scaledHeight, setScaledHeight] = useState<number | undefined>(undefined);
  const paginas = React.useMemo(() => splitPdfHtmlPages(html), [html]);
  const [paginaAtual, setPaginaAtual] = useState(0);

  useEffect(() => {
    setPaginaAtual(0);
  }, [html]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner) return;

    function update() {
      const width = wrapper!.getBoundingClientRect().width;
      const nextScale = width > 0 ? Math.min(1, width / PDF_HTML_PAGE_WIDTH) : 1;
      setScale(nextScale);
      setScaledHeight(inner!.scrollHeight * nextScale);
    }

    update();
    const observer = new ResizeObserver(update);
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [paginaAtual, paginas]);

  const totalPaginas = paginas.length;
  const mostrarNavegacao = totalPaginas > 1;

  function irPara(indice: number) {
    const alvo = Math.max(0, Math.min(totalPaginas - 1, indice));
    setPaginaAtual(alvo);
    wrapperRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  return (
    <div>
      {mostrarNavegacao && (
        <div
          className="flex items-center justify-between flex-wrap gap-3"
          style={{ marginBottom: '16px' }}
        >
          <button
            type="button"
            onClick={() => irPara(paginaAtual - 1)}
            disabled={paginaAtual === 0}
            className="btn"
            style={{
              padding: '8px 16px',
              border: '1px solid #d0d0d0',
              borderRadius: '6px',
              opacity: paginaAtual === 0 ? 0.4 : 1,
              cursor: paginaAtual === 0 ? 'default' : 'pointer',
            }}
          >
            {t('anterior')}
          </button>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {t('paginaDe', { atual: paginaAtual + 1, total: totalPaginas })}
          </span>
          <button
            type="button"
            onClick={() => irPara(paginaAtual + 1)}
            disabled={paginaAtual >= totalPaginas - 1}
            className="btn"
            style={{
              padding: '8px 16px',
              border: '1px solid #d0d0d0',
              borderRadius: '6px',
              opacity: paginaAtual >= totalPaginas - 1 ? 0.4 : 1,
              cursor: paginaAtual >= totalPaginas - 1 ? 'default' : 'pointer',
            }}
          >
            {t('proxima')}
          </button>
        </div>
      )}
      <div ref={wrapperRef} style={{ overflow: 'hidden', height: scaledHeight }}>
        <div
          ref={innerRef}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: PDF_HTML_PAGE_WIDTH,
          }}
          dangerouslySetInnerHTML={{ __html: paginas[paginaAtual] ?? '' }}
        />
      </div>
      {mostrarNavegacao && (
        <div
          className="flex items-center justify-between flex-wrap gap-3"
          style={{ marginTop: '16px' }}
        >
          <button
            type="button"
            onClick={() => irPara(paginaAtual - 1)}
            disabled={paginaAtual === 0}
            className="btn"
            style={{
              padding: '8px 16px',
              border: '1px solid #d0d0d0',
              borderRadius: '6px',
              opacity: paginaAtual === 0 ? 0.4 : 1,
              cursor: paginaAtual === 0 ? 'default' : 'pointer',
            }}
          >
            {t('anterior')}
          </button>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {t('paginaDe', { atual: paginaAtual + 1, total: totalPaginas })}
          </span>
          <button
            type="button"
            onClick={() => irPara(paginaAtual + 1)}
            disabled={paginaAtual >= totalPaginas - 1}
            className="btn"
            style={{
              padding: '8px 16px',
              border: '1px solid #d0d0d0',
              borderRadius: '6px',
              opacity: paginaAtual >= totalPaginas - 1 ? 0.4 : 1,
              cursor: paginaAtual >= totalPaginas - 1 ? 'default' : 'pointer',
            }}
          >
            {t('proxima')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Renderizacao do artigo em si. Recebe `artigo` ja resolvido (buscado no
 * servidor, em ArtigoPage) e `conteudo`/`tocItens` ja processados (ids
 * injetados nos h2/h3, sumario extraido) por `injetarIdsEExtrairToc` - ver
 * o comentario em src/lib/articleContent.ts para o motivo: assim o HTML
 * que o Next manda na resposta inicial (o que o Googlebot le sem executar
 * JS) ja contem o texto do artigo, os ids das secoes e o sumario, em vez
 * de depender de um fetch em useEffect (causa raiz de paginas reais sendo
 * classificadas como soft 404 no Search Console). Continua "use client"
 * porque o scrollspy (destacar a secao atual ao rolar) e o modo PDF
 * (PdfHtmlContent, com zoom/paginacao) precisam de estado e efeitos no
 * navegador - mas isso nao impede o HTML de ser enviado pronto no SSR;
 * "use client" so acrescenta a hidratacao, nao remove a renderizacao no
 * servidor.
 */
export default function ArtigoConteudo({
  artigo,
  conteudo,
  tocItens,
}: {
  artigo: Artigo;
  conteudo: string;
  tocItens: TocItem[];
}) {
  const t = useTranslations('paginaArtigo');
  const [tocAtivoId, setTocAtivoId] = useState<string | null>(null);

  // Scrollspy + scroll-to-hash: os ids ja vem prontos no DOM (injetados no
  // servidor), entao so falta observar a rolagem e, se a URL chegou com
  // #ancora, corrigir a posicao considerando o header fixo (o salto nativo
  // do navegador acontece antes do layout estabilizar).
  useEffect(() => {
    if (tocItens.length < 2) return;

    if (window.location.hash) {
      const alvo = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (alvo) {
        window.scrollTo({ top: alvo.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET });
      }
    }

    let agendado = false;
    function aoRolar() {
      if (agendado) return;
      agendado = true;
      window.requestAnimationFrame(() => {
        agendado = false;
        const linha = window.innerHeight * 0.33;
        let atual = 0;
        for (let i = 0; i < tocItens.length; i++) {
          const el = document.getElementById(tocItens[i].id);
          if (el && el.getBoundingClientRect().top <= linha) {
            atual = i;
          } else if (el) {
            break;
          }
        }
        setTocAtivoId(tocItens[atual]?.id ?? null);
      });
    }

    window.addEventListener('scroll', aoRolar, { passive: true });
    window.addEventListener('resize', aoRolar, { passive: true });
    aoRolar();
    return () => {
      window.removeEventListener('scroll', aoRolar);
      window.removeEventListener('resize', aoRolar);
    };
  }, [tocItens]);

  const temToc = tocItens.length >= 2;
  const ehPdf = isConteudoPdf(conteudo);

  return (
    <div
      className={`grid grid-cols-1 ${temToc ? 'artigo-grid-com-toc' : ''}`}
      style={{ gap: '48px' }}
    >
      <style>{`
        @media (min-width: 1024px) {
          .artigo-grid-com-toc { grid-template-columns: minmax(0, 1fr) 320px; }
        }
      `}</style>
      <article style={{ maxWidth: '760px' }}>
        <p className="eyebrow mb-3" style={{ color: '#1a9fff' }}>
          Artigo
        </p>
        <h1 className="display-section mb-4" style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}>
          {artigo.titulo}
        </h1>

        <div className="flex items-center flex-wrap justify-between gap-3 mb-8">
          <div className="flex items-center gap-3 text-gray-500" style={{ fontSize: '13px' }}>
            {artigo.autor && <span>{artigo.autor}</span>}
            {artigo.autor && artigo.publicadoEm && <span>&middot;</span>}
            {artigo.publicadoEm && <span>{dataArtigoBr(artigo.publicadoEm)}</span>}
          </div>
          <LanguageSwitcher />
        </div>

        {temToc && (
          <div className="mb-8">
            <style>{`
              @media (min-width: 1024px) { .artigo-toc-mobile { display: none !important; } }
            `}</style>
            <div className="artigo-toc-mobile">
              <ArtigoToc
                itens={tocItens}
                ativoId={tocAtivoId}
                titulo={t('nesteArtigo')}
                ariaLabel={t('sumarioAria')}
              />
            </div>
          </div>
        )}

        {/*
          Renderizado via dangerouslySetInnerHTML sem sanitizacao adicional
          no cliente. Normalmente isso seria um risco de XSS, mas aqui o
          HTML vem exclusivamente do painel administrativo (chefia),
          preenchido apenas por usuarios internos autenticados - nunca
          por entrada de usuarios publicos do site.
        */}
        {ehPdf && <PdfHtmlContent html={conteudo} />}

        {!ehPdf && conteudo && (
          <>
            <ArticleContentStyles />
            <div
              className="article-content text-gray-800"
              dangerouslySetInnerHTML={{ __html: conteudo }}
            />
          </>
        )}

        {artigo.ebookUrl && (
          <div
            className="mt-12 text-center"
            style={{ borderTop: '1px solid #e5e5e5', paddingTop: '40px' }}
          >
            <p className="text-gray-600 mb-5" style={{ fontSize: '15px' }}>
              Chegou ao final da matéria? Baixe o material completo em PDF.
            </p>
            <a
              href={artigo.ebookUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
              style={{ padding: '14px 28px' }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {artigo.ebookTitulo || t('baixarEbook')}
            </a>
          </div>
        )}
      </article>

      {temToc && (
        <aside
          className="hidden lg:block"
          style={{ position: 'sticky', top: '130px', alignSelf: 'start' }}
        >
          <ArtigoToc
            itens={tocItens}
            ativoId={tocAtivoId}
            titulo={t('nesteArtigo')}
            ariaLabel={t('sumarioAria')}
          />
        </aside>
      )}
    </div>
  );
}
